/**
 * Reseller Application form (Concept theme) — worker-backed.
 *
 * Self-contained <reseller-form-hub> custom element. Intercepts the form submit
 * and POSTs JSON to the Cloudflare worker's /submit-reseller endpoint, which
 * creates a Zendesk ticket tagged "reseller-application". Replaces the native
 * Shopify {% form 'contact' %} email flow on the Reseller Application page only
 * (the shared contact-form section is left untouched).
 *
 * Mirrors lead-form.js conventions: honeypot + minimum-fill-time bot guard,
 * retry-with-backoff, graceful success/error UI. Sends Content-Type text/plain
 * (the worker JSON.parses the raw body) to avoid a CORS preflight.
 *
 * Worker field contract: name, email, phone, company, use_case, message,
 * submitted_at, page_url. Required (server-side too): email, company, use_case.
 */
(function () {
  if (customElements.get('reseller-form-hub')) return;

  var MIN_FILL_MS = 3000;
  var MAX_ATTEMPTS = 3;
  var RETRY_BACKOFF_MS = 800;
  var REQUIRED = ['email', 'phone', 'company', 'use_case'];

  class ResellerFormHub extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this._init) return;
      this._init = true;

      this.endpoint = (this.getAttribute('data-endpoint') || '').trim();
      this.statusEl = this.querySelector('[data-reseller-status]');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.successMsg = this.getAttribute('data-success-message') ||
        'Thanks for applying — a DSLRPros partnerships specialist will be in touch shortly.';

      var ts = this.querySelector('[name="form_load_timestamp"]');
      if (ts) ts.value = String(Date.now());

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    val(name) {
      var el = this.form.elements[name];
      if (!el) return '';
      if (el.length && el[0] && el[0].type === 'radio') {
        for (var i = 0; i < el.length; i++) if (el[i].checked) return el[i].value;
        return '';
      }
      return (el.value || '').trim();
    }

    showStatus(type, message) {
      if (!this.statusEl) return;
      this.statusEl.innerHTML =
        '<div class="alert alert--' + type + ' flex items-start gap-3 text-sm md:text-base leading-tight">' +
        '<span>' + message + '</span></div>';
      this.statusEl.hidden = false;
    }

    clearStatus() {
      if (this.statusEl) { this.statusEl.innerHTML = ''; this.statusEl.hidden = true; }
    }

    setBusy(busy) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = busy;
      this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    validate() {
      for (var i = 0; i < REQUIRED.length; i++) {
        if (!this.val(REQUIRED[i])) {
          this.showStatus('error', 'Please complete all required fields before submitting.');
          var el = this.form.elements[REQUIRED[i]];
          var focusEl = el && el.length ? el[0] : el;
          if (focusEl && focusEl.focus) focusEl.focus();
          return false;
        }
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.val('email'))) {
        this.showStatus('error', 'Please enter a valid email address.');
        return false;
      }
      return true;
    }

    handleSubmit(event) {
      event.preventDefault();
      this.clearStatus();

      if (this.val('company_website')) { this.fakeSuccess(); return; }

      var openedAt = parseInt(this.val('form_load_timestamp') || '0', 10);
      if (!openedAt || (Date.now() - openedAt) < MIN_FILL_MS) { this.fakeSuccess(); return; }

      if (!this.validate()) return;

      if (!this.endpoint || !/^https:\/\//i.test(this.endpoint)) {
        console.error('[reseller-form] Missing/invalid endpoint URL:', this.endpoint);
        this.showStatus('error', 'Something went wrong. Please email dealers@dslrpros.com or call (877) 299-1075.');
        return;
      }

      var payload = {
        name: this.val('name'),
        email: this.val('email'),
        phone: this.val('phone'),
        company: this.val('company'),
        use_case: this.val('use_case'),
        message: this.val('message'),
        submitted_at: new Date().toISOString(),
        page_url: window.location.href
      };

      this.setBusy(true);
      this.postWithRetry(this.endpoint, payload, 1)
        .then(function () { this.onSuccess(); }.bind(this))
        .catch(function (err) {
          console.error('[reseller-form] Submission failed:', err && err.message);
          this.setBusy(false);
          this.showStatus('error', 'We could not submit your application. Please try again, or email dealers@dslrpros.com / call (877) 299-1075.');
        }.bind(this));
    }

    postWithRetry(url, payload, attempt) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
        keepalive: true
      })
        .then(function (res) {
          if (res.ok) return res.json().catch(function () { return {}; });
          var err = new Error('HTTP ' + res.status);
          err.httpStatus = res.status;
          throw err;
        })
        .catch(function (err) {
          var retry = attempt < MAX_ATTEMPTS && (!err.httpStatus || err.httpStatus >= 500);
          if (retry) {
            return new Promise(function (r) { setTimeout(r, RETRY_BACKOFF_MS * attempt); })
              .then(function () { return this.postWithRetry(url, payload, attempt + 1); }.bind(this));
          }
          throw err;
        }.bind(this));
    }

    onSuccess() {
      this.setBusy(false);
      this.form.reset();
      this.showStatus('success', this.successMsg);
      if (this.statusEl && this.statusEl.scrollIntoView) {
        this.statusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    fakeSuccess() {
      this.form.reset();
      this.showStatus('success', this.successMsg);
    }
  }

  customElements.define('reseller-form-hub', ResellerFormHub);
})();
